import {
  PORTAL_AUTH_EMAIL,
  PORTAL_AUTH_PASSWORD,
  PORTAL_BACKEND_URL,
} from './config';
import { toast } from 'sonner';

let cachedToken = null;
let authPromise = null;

const isSuccess = (status) => status >= 200 && status < 300;

async function authenticatePortal() {
  if (!PORTAL_BACKEND_URL) {
    throw new Error('REACT_APP_PORTAL_BACKEND_URL is not configured');
  }
  if (!PORTAL_AUTH_PASSWORD) {
    throw new Error('REACT_APP_PORTAL_AUTH_PASSWORD is not configured');
  }

  const response = await fetch(`${PORTAL_BACKEND_URL}/authenticate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: PORTAL_AUTH_EMAIL?.toLowerCase(),
      password: PORTAL_AUTH_PASSWORD,
    }),
  });

  if (!isSuccess(response.status)) {
    if (response.status === 401) {
      throw new Error('Portal authentication failed — check service account email and password');
    }
    if (response.status === 403) {
      throw new Error('Portal authentication failed — service account is inactive');
    }
    if (response.status === 423) {
      throw new Error('Portal authentication failed — service account is locked');
    }
    throw new Error(`Portal authentication failed (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!data?.jwttoken) {
    throw new Error('Portal authentication response missing token');
  }

  return data.jwttoken;
}

export async function getPortalAuthToken() {
  if (cachedToken) return cachedToken;

  if (!authPromise) {
    authPromise = authenticatePortal()
      .then((token) => {
        cachedToken = token;
        return token;
      })
      .finally(() => {
        authPromise = null;
      });
  }

  return authPromise;
}

export async function portalRequest(path, options = {}) {
  const token = await getPortalAuthToken();
  const { headers: customHeaders, body, ...rest } = options;

  const response = await fetch(`${PORTAL_BACKEND_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...customHeaders,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get('content-type') || '';
  const responseBody = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!isSuccess(response.status)) {
    const message = typeof responseBody === 'string'
      ? responseBody
      : responseBody?.message || 'Portal request failed';
    throw new Error(message);
  }

  return responseBody;
}

export function searchBenchmarkCompanies(companyName) {
  const params = new URLSearchParams({ companyName });
  return portalRequest(`/esg/benchmark/companies/search?${params}`);
}

export function submitBenchmarkDocument(payload) {
  return portalRequest('/esg/benchmark/documents/submit', { method: 'POST', body: payload });
}

export function listBenchmarkDocuments() {
  return portalRequest('/esg/benchmark/documents');
}

export function updateBenchmarkDocument(documentId, payload) {
  return portalRequest(`/esg/benchmark/documents/${documentId}/update`, {
    method: 'POST',
    body: payload,
  });
}

export async function generatePreSignedUrl({ storageAccountContainer, permission, blobName, blobPath }) {
  const body = {
    storageAccountContainer,
    permission,
    blobName,
    ...(blobPath && { blobPath }),
  };

  // ponytail: always read plain text — SAS token must not go through JSON content-type sniffing
  const token = await getPortalAuthToken();
  const response = await fetch(`${PORTAL_BACKEND_URL}/generatePreSignedURL`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const sasToken = await response.text();
  if (!isSuccess(response.status)) {
    throw new Error(sasToken || 'Failed to generate pre-signed URL');
  }

  return sasToken.trim();
}

// Makes the portal reload its in-memory conversion / startup maps (e.g. after a category conversion is approved).
// Fire and forget: the approval itself is already saved, so a failure only shows a warning.
export function reloadPortalStartupMaps() {
  return portalRequest('/internal/reload-startup-maps', { method: 'POST' })
    .then(() => toast.success('Portal startup maps reloaded'))
    .catch((error) => toast.warning(`Approved, but the portal maps reload failed (${error.message}). Trigger /internal/reload-startup-maps manually.`));
}
